import { ChangeDetectorRef, Component } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AccountActions, AccountSelectors } from '@recruitee/account';
import { CurrentUserSelectors } from '@recruitee/account-data';
import {
  filterBoolean,
  GeoPluginService,
  noWhitespaceValidator,
  subdomainValidator,
} from '@recruitee/common';
import { CompanyActions } from '@recruitee/company-data';
import { Company, CurrentUser } from '@recruitee/http-types';
import { I18nService } from '@recruitee/i18n';
import { LanguagesService, SupportedLanguage } from '@recruitee/languages';
import {
  DialogComponentI,
  DialogRef,
  RtStatefulComponent,
  SelectOption,
} from '@recruitee/user-interface';

type SubdomainFieldErrors = {
  required: string;
  subdomainPattern: string;
  minlength: string;
  taken: string;
};

type State = {
  loading: boolean;
  isValid: boolean;
};

const SUBDOMAIN_TAKEN_ERROR = 'Subdomain already taken';

@Component({
  selector: 'rt-new-company-dialog',
  templateUrl: './new-company-dialog.component.html',
  styleUrls: ['./new-company-dialog.component.less'],
})
export class NewCompanyDialogComponent
  extends RtStatefulComponent<State>
  implements DialogComponentI<{ noCurrentCompany?: boolean }, null>
{
  public newCompanyForm: UntypedFormGroup;
  public languageSelectOptions: Array<SelectOption<SupportedLanguage>>;
  public nameFieldErrors: { required: string; whitespace: string } = {
    required: this.i18n.translate('app.new_company_form.errors.required_name'),
    whitespace: this.i18n.translate('app.new_company_form.errors.required_name'),
  };
  public subdomainFieldErrors: SubdomainFieldErrors = {
    required: this.i18n.translate('app.new_company_form.errors.required_url'),
    subdomainPattern: this.i18n.translate('app.new_company_form.errors.invalid_character_error'),
    minlength: this.i18n.translate('app.new_company_form.errors.min_lenght'),
    taken: this.i18n.translate('app.settings.careers_site.errors.subdomain_taken'),
  };

  private noCurrentCompany: boolean = false;
  private ngOnDestroy$ = new Subject<void>();
  private currentCompany: Company;
  private currentAdmin: CurrentUser;

  private companyCreatedSuccess$ = this.actions$.pipe(ofType(CompanyActions.createSuccess));
  private companyCreatedFailed$ = this.actions$.pipe(ofType(CompanyActions.createFailed));

  constructor(
    private dialog: DialogRef,
    private formBuilder: UntypedFormBuilder,
    private i18n: I18nService,
    private geoPlugin: GeoPluginService,
    protected cdRef: ChangeDetectorRef,
    private store: Store,
    private actions$: Actions,
    private languagesService: LanguagesService,
  ) {
    super(cdRef);
    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(
        filterBoolean,
        withLatestFrom(this.store.select(CurrentUserSelectors.data)),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(([currentCompany, currentAdmin]) => {
        this.currentCompany = currentCompany;
        this.currentAdmin = currentAdmin!;
      });
    this.createState({ loading: false, isValid: false });

    this.languageSelectOptions = this.languagesService.languageSelectOptions;
  }

  public setDialogData(data: { noCurrentCompany?: boolean }, triggerUpdate: boolean): void {
    if (data.noCurrentCompany !== undefined) {
      this.noCurrentCompany = data.noCurrentCompany;
    }
  }

  // eslint-disable-next-line max-lines-per-function
  public ngOnInit(): void {
    let language = 'en';

    if (this.currentAdmin?.preferredLanguage === 'auto' && this.currentCompany) {
      language = this.currentCompany.language;
    }

    this.newCompanyForm = this.formBuilder.group({
      name: ['', Validators.compose([Validators.required, noWhitespaceValidator])],
      subdomain: new UntypedFormControl('', [
        Validators.required,
        subdomainValidator,
        Validators.minLength(3),
      ]),
      language: [language, Validators.required],
      fillWithSampleData: [true],
      countryCode: [''],
    });
    this.getCountyCode();

    this.newCompanyForm.valueChanges
      .pipe(debounceTime(150), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.setState({ ...this.state, isValid: this.newCompanyForm.valid });
      });

    this.companyCreatedSuccess$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(({ payload: company }) => {
        this.setState({ ...this.state, loading: false });
        this.dialog.close();
        this.store.dispatch(AccountActions.changeCompany({ payload: company }));
      });

    this.companyCreatedFailed$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(({ payload: { error } }) => {
        if (error?.errorFields?.subdomain?.filter((e: string) => e === SUBDOMAIN_TAKEN_ERROR)) {
          this.newCompanyForm.controls['subdomain'].setErrors({ taken: true });
        }
        this.setState({ ...this.state, loading: false });
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public submitClick(): void {
    const formData = this.newCompanyForm.getRawValue();
    this.setState({
      ...this.state,
      loading: true,
    });

    this.store.dispatch(
      CompanyActions.create({ payload: { body: formData, skipCompany: this.noCurrentCompany } }),
    );
  }

  public cancelClick(): void {
    this.dialog.close();
  }

  private getCountyCode(): void {
    this.geoPlugin.fetch.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(
      data => this.setCountryCode(data.country_code),
      () => this.setCountryCode(),
    );
  }

  private setCountryCode(countryCode?: string): void {
    if (countryCode) {
      this.newCompanyForm.get('countryCode')!.setValue(countryCode);
    } else {
      const currentCompanyCountryCode = this.currentCompany && this.currentCompany.countryCode;

      if (currentCompanyCountryCode) {
        this.newCompanyForm.get('countryCode')!.setValue(currentCompanyCountryCode);
      }
    }
  }
}
